import React, { useEffect, useMemo, useState } from 'react';
import { useInfiniteQuery, useQueries, useQuery } from '@tanstack/react-query';
import {
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import type { SxProps, TextFieldProps, Theme } from '@mui/material';

import { agentApi, expertApi, llmModelApi } from '../../services/api';
import type {
  AgentDetail,
  AgentListItem,
  ConversationSessionListItem,
  Expert,
  ExpertListItem,
  LlmModel,
} from '../../types';

const DEFAULT_PAGE_SIZE = 25;

const useDebouncedValue = (value: string, delayMs = 250) => {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const timeout = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(timeout);
  }, [delayMs, value]);

  return debounced;
};

const uniqueById = <T extends { id: number | string }>(items: T[]) => {
  const seen = new Set<number | string>();
  return items.filter((item) => {
    if (seen.has(item.id)) return false;
    seen.add(item.id);
    return true;
  });
};

const shouldLoadNextPage = (event: React.UIEvent<HTMLElement>) => {
  const listboxNode = event.currentTarget;
  const remaining =
    listboxNode.scrollHeight - listboxNode.scrollTop - listboxNode.clientHeight;
  return remaining < 80;
};

export interface LlmModelAutocompleteProps {
  value?: number | null | '';
  onChange: (modelId: number | null) => void;
  label?: string;
  placeholder?: string;
  helperText?: React.ReactNode;
  disabled?: boolean;
  required?: boolean;
  size?: 'small' | 'medium';
  fullWidth?: boolean;
  filters?: {
    provider?: number;
    is_active?: boolean;
    audio_input_supported?: boolean;
    direct_audio_input_supported?: boolean;
    transcription_supported?: boolean;
    audio_output_supported?: boolean;
    speech_synthesis_supported?: boolean;
  };
  pageSize?: number;
  sx?: SxProps<Theme>;
  textFieldProps?: Omit<TextFieldProps, 'value' | 'onChange' | 'label'>;
}

export const LlmModelAutocomplete: React.FC<LlmModelAutocompleteProps> = ({
  value,
  onChange,
  label = 'LLM model',
  placeholder = 'Search models...',
  helperText,
  disabled = false,
  required = false,
  size = 'small',
  fullWidth = true,
  filters,
  pageSize = DEFAULT_PAGE_SIZE,
  sx,
  textFieldProps,
}) => {
  const selectedId = typeof value === 'number' && value > 0 ? value : null;
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search.trim());

  const modelsQuery = useInfiniteQuery({
    queryKey: ['llm-model-selector', filters || {}, debouncedSearch, pageSize],
    queryFn: ({ pageParam }) =>
      llmModelApi.list({
        ...(filters || {}),
        page: pageParam,
        page_size: pageSize,
        search: debouncedSearch || undefined,
      }),
    enabled: open && !disabled,
    initialPageParam: 1,
    getNextPageParam: (lastPage, pages) => (lastPage.next ? pages.length + 1 : undefined),
  });

  const loadedModels = useMemo(
    () => modelsQuery.data?.pages.flatMap((page) => page.results || []) || [],
    [modelsQuery.data]
  );

  const selectedModelInList =
    selectedId != null ? loadedModels.find((model) => model.id === selectedId) : null;

  const selectedModelQuery = useQuery({
    queryKey: ['llm-model-selector-selected', selectedId],
    queryFn: () => llmModelApi.get(selectedId as number),
    enabled: selectedId != null && !selectedModelInList,
    staleTime: 5 * 60 * 1000,
  });

  const options = useMemo(
    () => uniqueById([selectedModelQuery.data, ...loadedModels].filter(Boolean) as LlmModel[]),
    [loadedModels, selectedModelQuery.data]
  );

  const selectedModel =
    selectedId != null ? options.find((model) => model.id === selectedId) || null : null;
  const loading =
    modelsQuery.isLoading || modelsQuery.isFetchingNextPage || selectedModelQuery.isFetching;

  return (
    <Autocomplete
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => {
        setOpen(false);
        setSearch('');
      }}
      options={options}
      value={selectedModel}
      loading={loading}
      disabled={disabled}
      fullWidth={fullWidth}
      size={size}
      sx={sx}
      filterOptions={(items) => items}
      noOptionsText={debouncedSearch ? 'No matching models' : 'No models available'}
      getOptionLabel={(option) =>
        option ? `${option.name}${option.provider_name ? ` (${option.provider_name})` : ''}` : ''
      }
      isOptionEqualToValue={(option, selected) => option.id === selected.id}
      onInputChange={(_, nextInput, reason) => {
        if (reason === 'input') setSearch(nextInput);
        if (reason === 'clear') setSearch('');
      }}
      onChange={(_, option) => onChange(option?.id ?? null)}
      disableClearable={required}
      ListboxProps={{
        style: { maxHeight: 320 },
        onScroll: (event) => {
          if (
            shouldLoadNextPage(event) &&
            modelsQuery.hasNextPage &&
            !modelsQuery.isFetchingNextPage
          ) {
            modelsQuery.fetchNextPage();
          }
        },
      }}
      renderOption={(props, option) => (
        <Box component="li" {...props}>
          <Stack spacing={0.25} sx={{ minWidth: 0 }}>
            <Typography variant="body2" noWrap>
              {option.name}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              {option.provider_name || 'Unknown provider'} - {option.model}
            </Typography>
          </Stack>
        </Box>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          {...textFieldProps}
          label={label}
          placeholder={placeholder}
          required={required}
          helperText={helperText ?? textFieldProps?.helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

export interface AgentSelectorOption {
  id: number;
  name: string;
  description?: string | null;
  agent_type?: string;
  model_name?: string | null;
  is_active?: boolean;
  created_at?: string;
  tool_count?: number;
  capabilities?: string[];
}

const toAgentOption = (agent: AgentListItem | AgentDetail | AgentSelectorOption): AgentSelectorOption => ({
  id: agent.id,
  name: agent.name,
  description: agent.description,
  agent_type: agent.agent_type,
  model_name:
    'model_name' in agent && agent.model_name
      ? agent.model_name
      : 'model' in agent && agent.model
        ? agent.model.name
        : null,
  is_active: agent.is_active,
  created_at: agent.created_at,
  tool_count: agent.tool_count,
  capabilities: agent.capabilities,
});

export interface AgentAutocompleteProps {
  value?: number | null | '';
  onChange: (agentId: number | null) => void;
  onOptionChange?: (agent: AgentSelectorOption | null) => void;
  label?: string;
  placeholder?: string;
  helperText?: React.ReactNode;
  disabled?: boolean;
  required?: boolean;
  size?: 'small' | 'medium';
  fullWidth?: boolean;
  filters?: {
    is_active?: boolean;
    is_template?: boolean;
    agent_type?: string;
  };
  pageSize?: number;
  sx?: SxProps<Theme>;
  startAdornment?: React.ReactNode;
  initialOption?: AgentSelectorOption | null;
  textFieldProps?: Omit<TextFieldProps, 'value' | 'onChange' | 'label'>;
}

export const AgentAutocomplete: React.FC<AgentAutocompleteProps> = ({
  value,
  onChange,
  onOptionChange,
  label = 'Agent',
  placeholder = 'Search agents...',
  helperText,
  disabled = false,
  required = false,
  size = 'small',
  fullWidth = true,
  filters,
  pageSize = DEFAULT_PAGE_SIZE,
  sx,
  startAdornment,
  initialOption = null,
  textFieldProps,
}) => {
  const selectedId = typeof value === 'number' && value > 0 ? value : null;
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search.trim());

  const agentsQuery = useInfiniteQuery({
    queryKey: ['agent-selector', filters || {}, debouncedSearch, pageSize],
    queryFn: ({ pageParam }) =>
      agentApi.list(pageParam, pageSize, {
        ...(filters || {}),
        search: debouncedSearch || undefined,
      }),
    enabled: open && !disabled,
    initialPageParam: 1,
    getNextPageParam: (lastPage, pages) => (lastPage.next ? pages.length + 1 : undefined),
  });

  const loadedAgents = useMemo(
    () =>
      agentsQuery.data?.pages.flatMap((page) =>
        (page.results || []).map((agent) => toAgentOption(agent))
      ) || [],
    [agentsQuery.data]
  );

  const selectedAgentInList =
    selectedId != null ? loadedAgents.find((agent) => agent.id === selectedId) : null;

  const selectedAgentQuery = useQuery({
    queryKey: ['agent-selector-selected', selectedId],
    queryFn: async () => toAgentOption(await agentApi.get(selectedId as number)),
    enabled: selectedId != null && !selectedAgentInList && !initialOption,
    staleTime: 5 * 60 * 1000,
  });

  const options = useMemo(
    () =>
      uniqueById(
        [initialOption, selectedAgentQuery.data, ...loadedAgents].filter(
          Boolean
        ) as AgentSelectorOption[]
      ),
    [initialOption, loadedAgents, selectedAgentQuery.data]
  );

  const selectedAgent =
    selectedId != null ? options.find((agent) => agent.id === selectedId) || null : null;
  const loading =
    agentsQuery.isLoading || agentsQuery.isFetchingNextPage || selectedAgentQuery.isFetching;

  return (
    <Autocomplete
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => {
        setOpen(false);
        setSearch('');
      }}
      options={options}
      value={selectedAgent}
      loading={loading}
      disabled={disabled}
      fullWidth={fullWidth}
      size={size}
      sx={sx}
      filterOptions={(items) => items}
      noOptionsText={debouncedSearch ? 'No matching agents' : 'No agents available'}
      getOptionLabel={(option) => option?.name || ''}
      isOptionEqualToValue={(option, selected) => option.id === selected.id}
      onInputChange={(_, nextInput, reason) => {
        if (reason === 'input') setSearch(nextInput);
        if (reason === 'clear') setSearch('');
      }}
      onChange={(_, option) => {
        onChange(option?.id ?? null);
        onOptionChange?.(option ?? null);
      }}
      disableClearable={required}
      ListboxProps={{
        style: { maxHeight: 320 },
        onScroll: (event) => {
          if (
            shouldLoadNextPage(event) &&
            agentsQuery.hasNextPage &&
            !agentsQuery.isFetchingNextPage
          ) {
            agentsQuery.fetchNextPage();
          }
        },
      }}
      renderOption={(props, option) => (
        <Box component="li" {...props}>
          <Stack spacing={0.4} sx={{ minWidth: 0, width: '100%' }}>
            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
              <Typography variant="body2" noWrap sx={{ minWidth: 0 }}>
                {option.name}
              </Typography>
              {option.agent_type ? (
                <Chip size="small" variant="outlined" label={option.agent_type} />
              ) : null}
            </Stack>
            <Typography variant="caption" color="text.secondary" noWrap>
              {option.model_name || 'No model'}{option.description ? ` - ${option.description}` : ''}
            </Typography>
          </Stack>
        </Box>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          {...textFieldProps}
          label={label}
          placeholder={placeholder}
          required={required}
          helperText={helperText ?? textFieldProps?.helperText}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <>
                {startAdornment}
                {params.InputProps.startAdornment}
              </>
            ),
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

export interface ExpertSelectorOption {
  id: number;
  name: string;
  description?: string | null;
  expert_type?: string;
  is_active?: boolean;
}

const toExpertOption = (
  expert: Expert | ExpertListItem | ExpertSelectorOption
): ExpertSelectorOption => ({
  id: expert.id,
  name: expert.name,
  description: expert.description,
  expert_type: expert.expert_type,
  is_active: expert.is_active,
});

export interface ExpertMultiAutocompleteProps {
  value?: number[];
  onChange: (expertIds: number[]) => void;
  aiSystemId?: number | null;
  label?: string;
  placeholder?: string;
  helperText?: React.ReactNode;
  disabled?: boolean;
  size?: 'small' | 'medium';
  fullWidth?: boolean;
  pageSize?: number;
  sx?: SxProps<Theme>;
}

export const ExpertMultiAutocomplete: React.FC<ExpertMultiAutocompleteProps> = ({
  value = [],
  onChange,
  aiSystemId,
  label = 'Experts',
  placeholder = 'Search experts...',
  helperText,
  disabled = false,
  size = 'medium',
  fullWidth = true,
  pageSize = DEFAULT_PAGE_SIZE,
  sx,
}) => {
  const selectedIds = value.filter((id) => Number.isInteger(id) && id > 0);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search.trim());
  const canLoad = Boolean(aiSystemId) && !disabled;

  const expertsQuery = useInfiniteQuery({
    queryKey: [
      'expert-selector',
      aiSystemId ?? null,
      debouncedSearch,
      pageSize,
    ],
    queryFn: ({ pageParam }) =>
      expertApi.list({
        ai_system: aiSystemId as number,
        is_active: true,
        search: debouncedSearch || undefined,
        page: pageParam,
        page_size: pageSize,
      }),
    enabled: open && canLoad,
    initialPageParam: 1,
    getNextPageParam: (lastPage, pages) =>
      lastPage.next ? pages.length + 1 : undefined,
  });

  const loadedExperts = useMemo(
    () =>
      expertsQuery.data?.pages.flatMap((page) =>
        (page.results || []).map(toExpertOption)
      ) || [],
    [expertsQuery.data]
  );
  const loadedIds = new Set(loadedExperts.map((expert) => expert.id));
  const missingSelectedIds = selectedIds.filter((id) => !loadedIds.has(id));
  const selectedExpertQueries = useQueries({
    queries: missingSelectedIds.map((id) => ({
      queryKey: ['expert-selector-selected', id],
      queryFn: async () => toExpertOption(await expertApi.get(id)),
      staleTime: 5 * 60 * 1000,
    })),
  });

  const options = useMemo(
    () =>
      uniqueById([
        ...selectedExpertQueries
          .map((query) => query.data)
          .filter(Boolean) as ExpertSelectorOption[],
        ...loadedExperts,
      ]),
    [loadedExperts, selectedExpertQueries]
  );
  const selectedExperts = selectedIds
    .map((id) => options.find((expert) => expert.id === id))
    .filter(Boolean) as ExpertSelectorOption[];
  const loading =
    expertsQuery.isLoading ||
    expertsQuery.isFetchingNextPage ||
    selectedExpertQueries.some((query) => query.isFetching);

  return (
    <Autocomplete
      multiple
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => {
        setOpen(false);
        setSearch('');
      }}
      options={options}
      value={selectedExperts}
      loading={loading}
      disabled={!canLoad}
      fullWidth={fullWidth}
      size={size}
      sx={sx}
      filterOptions={(items) => items}
      noOptionsText={debouncedSearch ? 'No matching experts' : 'No experts available'}
      getOptionLabel={(option) => option?.name || ''}
      isOptionEqualToValue={(option, selected) => option.id === selected.id}
      onInputChange={(_, nextInput, reason) => {
        if (reason === 'input') setSearch(nextInput);
        if (reason === 'clear') setSearch('');
      }}
      onChange={(_, selected) => onChange(selected.map((expert) => expert.id))}
      ListboxProps={{
        style: { maxHeight: 320 },
        onScroll: (event) => {
          if (
            shouldLoadNextPage(event) &&
            expertsQuery.hasNextPage &&
            !expertsQuery.isFetchingNextPage
          ) {
            expertsQuery.fetchNextPage();
          }
        },
      }}
      renderTags={(selected, getTagProps) =>
        selected.map((expert, index) => (
          <Chip
            {...getTagProps({ index })}
            key={expert.id}
            size="small"
            label={expert.name}
          />
        ))
      }
      renderOption={(props, option) => {
        const { key, ...optionProps } = props;
        return (
          <Box component="li" key={key} {...optionProps}>
            <Stack spacing={0.25} sx={{ minWidth: 0 }}>
              <Typography variant="body2" noWrap>
                {option.name}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap>
                {option.expert_type || 'Expert'}
                {option.description ? ` - ${option.description}` : ''}
              </Typography>
            </Stack>
          </Box>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          helperText={
            helperText ||
            (!aiSystemId
              ? 'Select an AI system before choosing experts'
              : undefined)
          }
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

const cleanConversationLabel = (value?: string | null) =>
  String(value || '')
    .replace(/[#*_`[\]]/g, '')
    .replace(/\s+/g, ' ')
    .trim();

const conversationDisplayTitle = (session: ConversationSessionListItem) =>
  cleanConversationLabel(session.title) ||
  cleanConversationLabel(session.first_message_preview) ||
  `Conversation ${session.id.slice(0, 8)}`;

export interface ConversationAutocompleteProps {
  value?: string | null | '';
  onChange: (sessionId: string | null) => void;
  agentId?: number | null;
  aiSystemId?: number | null;
  activeConversationId?: string | null;
  label?: string;
  placeholder?: string;
  helperText?: React.ReactNode;
  disabled?: boolean;
  size?: 'small' | 'medium';
  fullWidth?: boolean;
  pageSize?: number;
  sx?: SxProps<Theme>;
}

export const ConversationAutocomplete: React.FC<ConversationAutocompleteProps> = ({
  value,
  onChange,
  agentId,
  aiSystemId,
  activeConversationId,
  label = 'Continue in conversation (optional)',
  placeholder = 'Search conversations...',
  helperText,
  disabled = false,
  size = 'medium',
  fullWidth = true,
  pageSize = DEFAULT_PAGE_SIZE,
  sx,
}) => {
  const selectedId = value || null;
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search.trim());
  const canLoad = Boolean(agentId) && !disabled;

  const sessionsQuery = useInfiniteQuery({
    queryKey: [
      'conversation-selector',
      agentId ?? null,
      aiSystemId ?? null,
      activeConversationId ?? null,
      debouncedSearch,
      pageSize,
    ],
    queryFn: ({ pageParam }) =>
      agentApi.getSessions(
        agentId as number,
        undefined,
        pageParam,
        pageSize,
        aiSystemId || undefined,
        {
          search: debouncedSearch || undefined,
          pinned_session_id: activeConversationId || undefined,
        }
      ),
    enabled: open && canLoad,
    initialPageParam: 1,
    getNextPageParam: (lastPage, pages) =>
      lastPage.next ? pages.length + 1 : undefined,
  });

  const loadedSessions = useMemo(
    () => sessionsQuery.data?.pages.flatMap((page) => page.results || []) || [],
    [sessionsQuery.data]
  );
  const selectedSessionInList = selectedId
    ? loadedSessions.find((session) => session.id === selectedId)
    : null;

  const selectedSessionQuery = useQuery({
    queryKey: [
      'conversation-selector-selected',
      selectedId,
      agentId ?? null,
      aiSystemId ?? null,
    ],
    queryFn: async () => {
      const response = await agentApi.getSessions(
        agentId as number,
        undefined,
        1,
        1,
        aiSystemId || undefined,
        { session_id: selectedId }
      );
      return response.results[0] || null;
    },
    enabled: Boolean(selectedId && agentId && !selectedSessionInList),
    staleTime: 5 * 60 * 1000,
  });

  const options = useMemo(() => {
    const combined = uniqueById(
      [selectedSessionQuery.data, ...loadedSessions].filter(
        Boolean
      ) as ConversationSessionListItem[]
    );
    if (!activeConversationId) return combined;
    return [...combined].sort((left, right) => {
      if (left.id === activeConversationId) return -1;
      if (right.id === activeConversationId) return 1;
      return 0;
    });
  }, [activeConversationId, loadedSessions, selectedSessionQuery.data]);

  const selectedSession = selectedId
    ? options.find((session) => session.id === selectedId) || null
    : null;
  const loading =
    sessionsQuery.isLoading ||
    sessionsQuery.isFetchingNextPage ||
    selectedSessionQuery.isFetching;

  return (
    <Autocomplete
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => {
        setOpen(false);
        setSearch('');
      }}
      options={options}
      value={selectedSession}
      loading={loading}
      disabled={!canLoad}
      fullWidth={fullWidth}
      size={size}
      sx={sx}
      filterOptions={(items) => items}
      noOptionsText={
        debouncedSearch ? 'No matching conversations' : 'No conversations available'
      }
      getOptionLabel={conversationDisplayTitle}
      isOptionEqualToValue={(option, selected) => option.id === selected.id}
      onInputChange={(_, nextInput, reason) => {
        if (reason === 'input') setSearch(nextInput);
        if (reason === 'clear') setSearch('');
      }}
      onChange={(_, option) => onChange(option?.id ?? null)}
      ListboxProps={{
        style: { maxHeight: 320 },
        onScroll: (event) => {
          if (
            shouldLoadNextPage(event) &&
            sessionsQuery.hasNextPage &&
            !sessionsQuery.isFetchingNextPage
          ) {
            sessionsQuery.fetchNextPage();
          }
        },
      }}
      renderOption={(props, option) => {
        const { key, ...optionProps } = props;
        return (
          <Box component="li" key={key} {...optionProps}>
            <Stack spacing={0.4} sx={{ minWidth: 0, width: '100%' }}>
              <Stack direction="row" spacing={0.75} alignItems="center">
                <Typography variant="body2" noWrap sx={{ minWidth: 0 }}>
                  {conversationDisplayTitle(option)}
                </Typography>
                {option.id === activeConversationId ? (
                  <Chip size="small" color="primary" label="Active conversation" />
                ) : null}
                {!option.is_active ? (
                  <Chip size="small" variant="outlined" label="Archived" />
                ) : null}
              </Stack>
              <Typography variant="caption" color="text.secondary" noWrap>
                {option.last_message_at
                  ? `Last activity ${new Date(option.last_message_at).toLocaleString()}`
                  : 'No messages yet'}
                {option.message_count != null
                  ? ` · ${option.message_count} messages`
                  : ''}
              </Typography>
            </Stack>
          </Box>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          helperText={
            helperText ||
            (!agentId
              ? 'Select an agent before choosing a conversation'
              : 'Leave empty to start a fresh conversation for each run')
          }
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};
